import styled, { keyframes, css } from 'styled-components';

// Animações
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const glowPulse = keyframes`
  0% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.1); }
  50% { box-shadow: 0 0 15px rgba(64, 128, 255, 0.3); }
  100% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.1); }
`;

const checkmarkAnimation = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.7; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const dataFlow = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const borderPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(64, 128, 255, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(64, 128, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(64, 128, 255, 0); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const pulseCircle = keyframes`
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.8; }
  50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.3; }
  100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.8; }
`;

const progressGradient = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const completeGlow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
  }
  50% {
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.8);
  }
  100% {
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
  }
`;

const progressAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Componentes estilizados
export const PlatformContainer = styled.div`
  position: relative;
  background: rgba(15, 25, 45, 0.8);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(64, 128, 255, 0.2);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: ${css`${fadeIn} 0.5s ease-out`};
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    transform: translateY(-2px);
  }
`;

export const TechGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(64, 128, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(64, 128, 255, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
  z-index: 0;
  pointer-events: none;
`;

export const DataStream = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 2px;
    height: 40px;
    background: linear-gradient(to bottom, transparent, rgba(64, 128, 255, 0.5), transparent);
    animation: ${css`${dataFlow} 8s infinite`};
    opacity: 0;
  }
  
  &::before {
    left: 20%;
    animation-delay: 0s;
  }
  
  &::after {
    left: 80%;
    animation-delay: 4s;
  }
`;

export const PlatformHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 1;
`;

export const PlatformIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.5rem;
  color: ${props => props.color || '#4C6EF5'};
  border: 2px solid ${props => props.color || '#4C6EF5'};
  position: relative;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  
  svg {
    filter: drop-shadow(0 0 5px ${props => props.color || '#4C6EF5'});
  }
`;

export const TechCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 1px solid ${props => props.color || 'rgba(64, 128, 255, 0.3)'};
  animation: ${props => css`${pulse} 3s infinite`};
  animation-delay: ${props => props.delay || 0}s;
  opacity: 0.3;
`;

export const PlatformTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
`;

export const ProgressContainer = styled.div`
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
`;

export const ProgressBar = styled.div`
  height: 10px;
  background: rgba(15, 25, 45, 0.8);
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  margin-bottom: 0.5rem;
  border: 1px solid rgba(64, 128, 255, 0.2);
  box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: calc(100% / 5) 100%;
    z-index: 1;
    pointer-events: none;
  }
`;

export const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.width || 0}%;
  background: ${props => {
    // Cores específicas para cada plataforma
    let baseColor = props.color || '#4C6EF5';
    let gradientColor;
    
    // Definir cores de gradiente baseadas na plataforma
    if (baseColor === '#F22F46') { // Twilio
      gradientColor = '#FF4D6D';
    } else if (baseColor === '#00A67E') { // Vapi
      gradientColor = '#00D9A6';
    } else { // OpenAI ou padrão
      gradientColor = '#6C8FFF';
    }
    
    return `linear-gradient(90deg, ${baseColor}, ${gradientColor}, ${baseColor})`;
  }};
  background-size: 200% 100%;
  border-radius: 4px;
  transition: width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  animation: ${progressGradient} 3s ease infinite;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    background-size: 200% 100%;
    animation: ${css`${shimmer} 2s infinite`};
  }
  
  /* Adicionar efeito de brilho quando o progresso estiver completo */
  ${props => props.width === 100 && css`
    background: ${props => {
      // Cores específicas para cada plataforma quando completo
      let baseColor = props.color || '#4C6EF5';
      let brightColor;
      
      // Definir cores de gradiente baseadas na plataforma
      if (baseColor === '#F22F46') { // Twilio
        brightColor = '#FF7A90';
      } else if (baseColor === '#00A67E') { // Vapi
        brightColor = '#00FFB8';
      } else { // OpenAI ou padrão
        brightColor = '#90AAFF';
      }
      
      return `linear-gradient(90deg, ${baseColor}, ${brightColor}, ${baseColor})`;
    }};
    animation: ${progressGradient} 2s ease infinite, ${completeGlow} 2s infinite;
    box-shadow: 0 0 15px ${props.color || '#4C6EF5'};
    
    &::after {
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
      animation: ${shimmer} 1.5s infinite;
    }
  `}
`;

export const ProgressText = styled.div`
  font-size: 0.9rem;
  color: ${props => props.color || '#4C6EF5'};
  text-align: right;
  font-weight: 600;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
  letter-spacing: 0.5px;
`;

export const StepsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  position: relative;
  z-index: 1;
`;

export const StepItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.8rem;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  position: relative;
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border: 1px solid ${props => props.completed 
    ? 'rgba(0, 230, 118, 0.3)' 
    : props.active 
      ? 'rgba(64, 128, 255, 0.3)' 
      : 'rgba(64, 128, 255, 0.1)'};
  animation: ${fadeIn} 0.5s ease-out;
  
  ${props => props.active && `
    transform: scale(1.02);
    box-shadow: 0 0 20px rgba(64, 128, 255, 0.2);
  `}
  
  ${props => props.completed && `
    background: rgba(0, 0, 0, 0.15);
    box-shadow: 0 0 15px rgba(0, 230, 118, 0.1);
  `}
  
  &:hover {
    background: rgba(0, 0, 0, 0.25);
    transform: translateY(-2px);
  }
`;

export const AnimatedBorder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 12px;
  pointer-events: none;
  border: 2px solid ${props => props.completed 
    ? 'rgba(0, 230, 118, 0.6)' 
    : props.color || '#4C6EF5'};
  opacity: ${props => props.active ? 0.6 : 0};
  transition: opacity 0.3s ease, border-color 0.3s ease;
  box-shadow: ${props => props.active 
    ? `0 0 15px rgba(${props.completed ? '0, 230, 118' : '64, 128, 255'}, 0.2)` 
    : 'none'};
`;

export const StepNumber = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.completed 
    ? 'linear-gradient(to right, #00C853, #00E676)' 
    : 'rgba(15, 25, 45, 0.8)'};
  color: ${props => props.completed ? '#fff' : props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.2rem;
  font-weight: 600;
  flex-shrink: 0;
  border: 2px solid ${props => props.completed 
    ? '#00E676' 
    : props.color || '#4C6EF5'};
  box-shadow: ${props => props.completed 
    ? '0 0 10px rgba(0, 230, 118, 0.3)' 
    : '0 0 10px rgba(0, 0, 0, 0.2)'};
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  
  &::before {
    content: ${props => props.completed ? "''" : `"${props.stepNumber || ''}"`};
    position: relative;
    z-index: 2;
  }
  
  ${props => props.completed && css`
    &::after {
      content: '✓';
      position: absolute;
      font-size: 1.1rem;
      animation: ${checkmarkAnimation} 0.5s forwards;
      z-index: 2;
    }
  `}
`;

export const CompletedCheckmark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${css`${checkmarkAnimation} 0.5s forwards`};
  font-size: 1.1rem;
  color: #fff;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
`;

export const StepContent = styled.div`
  flex: 1;
  color: rgba(255, 255, 255, 0.9);
`;

export const StepDescription = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.8rem;
  line-height: 1.6;
  max-width: 100%;
  overflow-wrap: break-word;
  white-space: normal;
  padding-right: 0.5rem;
`;

export const StepText = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
`;

export const StepLink = styled.a`
  display: inline-block;
  color: ${props => props.color || '#4C6EF5'};
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  margin-top: 0.5rem;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid ${props => props.color || '#4C6EF5'};
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(76, 110, 245, 0.1);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

export const StepToggle = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.completed ? props.color : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.color || '#4C6EF5'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 1rem;
  transition: all 0.3s ease;
  color: white;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  }
  
  ${props => props.completed && css`
    background: ${props.color};
  `}
  
  svg {
    font-size: 0.8rem;
    animation: ${css`${checkmarkAnimation} 0.5s forwards`};
  }
`;

export const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  margin-top: 2rem;
  border-left: 3px solid ${props => props.color || '#4C6EF5'};
  
  svg {
    color: ${props => props.color || '#4C6EF5'};
    margin-right: 1rem;
    font-size: 1.2rem;
  }
  
  span {
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.4;
  }
`;

export const FinalSuccessMessage = styled.div`
  text-align: center;
  padding: 2rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  margin-top: 2rem;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(64, 128, 255, 0.3);
  
  .success-icon {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: linear-gradient(135deg, #4C6EF5, #00A67E);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: white;
    box-shadow: 0 0 20px rgba(76, 110, 245, 0.5);
  }
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: white;
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 1.5rem;
  }
  
  .pulse-container {
    position: relative;
    width: 100px;
    height: 100px;
    margin: 0 auto;
  }
  
  .pulse-circle {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 2px solid rgba(64, 128, 255, 0.5);
    animation: ${css`${pulseCircle} 2s infinite`};
    
    &:nth-child(2) {
      animation-delay: 0.5s;
    }
    
    &:nth-child(3) {
      animation-delay: 1s;
    }
  }
`;

export const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  position: relative;
  z-index: 1;
`;

export const NavigationButton = styled.button`
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export const PulsingDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color || '#4C6EF5'};
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 10px ${props => props.color || '#4C6EF5'};
  animation: ${css`${borderPulse} 2s infinite`};
  display: ${props => props.active ? 'block' : 'none'};
  z-index: 2;
  
  /* Garantir que o ponto nunca fique fora da barra */
  left: ${props => {
    const pos = props.position || 0;
    // Limitar a posição para que o ponto não ultrapasse os limites da barra
    // Deixar uma margem de 6px para garantir que o ponto fique totalmente dentro da barra
    return `min(${pos}%, calc(100% - 6px))`;
  }} !important;
  
  /* Desaparecer gradualmente quando o progresso estiver próximo de 100% */
  opacity: ${props => {
    const pos = props.position || 0;
    // Começar a desaparecer quando o progresso atingir 95%
    return pos >= 95 ? (100 - pos) / 5 : 1;
  }};
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: transparent;
    border: 2px solid ${props => props.color || '#4C6EF5'};
    transform: translate(-50%, -50%);
    animation: ${pulseCircle} 2s infinite;
    opacity: 0.5;
  }
`;

export default {
  PlatformContainer,
  PlatformHeader,
  PlatformIcon,
  PlatformTitle,
  ProgressContainer,
  ProgressBar,
  ProgressFill,
  ProgressText,
  StepsList,
  StepItem,
  StepNumber,
  StepContent,
  StepDescription,
  StepText,
  StepLink,
  CompletedCheckmark,
  StepToggle,
  TechCircle,
  PulsingDot,
  SuccessMessage,
  AnimatedBorder,
  TechGrid,
  DataStream,
  NavigationButtons,
  NavigationButton,
  FinalSuccessMessage
};
